/**
 * Saving a file the browser cannot fetch for itself.
 *
 * Every download in this app is behind a bearer token, and nothing in a browser that loads a URL by
 * itself - an `<a download>`, an `<img>`, a plain navigation - can send an Authorization header. So
 * the bytes come through `requestBlob`, which carries the token and the refresh-and-replay with it,
 * and the blob is handed to the browser as an object URL.
 *
 * The OpenSilver client did this with ten copy-pasted `ExecuteJavaScriptVoid` string-interpolated
 * IIFEs - each of which put the live access token into a JavaScript string literal. One function,
 * no interpolation, and the token never leaves the fetch.
 */
import { ApiError, API_BASE, bearer, ensureFresh, errorFrom } from './api/auth.js';
import { requestBlob } from './api/http.js';

/** Hands the browser a blob to save under `fileName`. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn rather than immediately: Safari has been known to cancel a download
  // whose object URL is released in the same task as the click.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Fetches an authenticated URL and saves it. Throws what `request` throws, for the caller's status line. */
export async function download(path: string, fileName: string): Promise<void> {
  saveBlob(await requestBlob(path), fileName);
}

/**
 * Opens an authenticated file - an invoice PDF - in a new tab.
 *
 * THE TAB IS OPENED BEFORE THE FETCH, and that order is the whole function. A window opened after an
 * `await` has lost the click's user activation and every browser blocks it as a pop-up; opening a
 * blank one first, synchronously inside the click, and pointing it at the blob once the bytes arrive
 * is what gets through. Must therefore be called from the click handler itself, not after an await.
 * Throws what `request` throws; the blank tab is closed rather than left behind on a failure. Where
 * the pop-up is blocked regardless, the file is saved under `fallbackName` instead.
 */
export async function openInNewTab(path: string, fallbackName: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const blob = await requestBlob(path);
    // Blocked anyway: save it instead. Navigating THIS tab to the blob would unload the CRM.
    if (!tab) {
      saveBlob(blob, fallbackName);
      return;
    }
    const url = URL.createObjectURL(blob);
    tab.location.href = url;
    // Long enough for the viewer to have loaded it; a PDF tab keeps its own copy once open.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/**
 * A file name built from a customer or device name.
 *
 * Letters, digits, hyphen and underscore only - the same rule the C# `Sanitize` applies, because
 * these names end up on a USB stick and in a Windows download folder.
 */
export function safeFileName(name: string | null | undefined, fallback = 'customer'): string {
  const kept = (name ?? '').replace(/[^A-Za-z0-9_-]/g, '');
  return kept.length > 0 ? kept : fallback;
}

/**
 * Asks the browser for files.
 *
 * Resolves to an empty array when the picker is dismissed - and note it may never resolve at all if
 * the person simply leaves the dialog open, which is the browser's behavior and not something a page
 * can detect. The input is created, clicked and discarded rather than kept: a hidden `<input>` left
 * in the document holds the last selection, so the second upload would silently re-send the first
 * one's files if the dialog were cancelled.
 */
export function pickFiles(multiple = true, accept = ''): Promise<File[]> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    if (accept) input.accept = accept;
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('change', () => {
      const files = [...(input.files ?? [])];
      input.remove();
      resolve(files);
    });
    // `cancel` is not universal, so a dismissed picker may leave this promise pending. The caller's
    // status line therefore says nothing until files really arrive.
    input.addEventListener('cancel', () => {
      input.remove();
      resolve([]);
    });
    input.click();
  });
}

/**
 * Uploads files to an authenticated endpoint, reporting progress. Resolves with the response body,
 * for the endpoints that answer a refusal as a 200 carrying its reason.
 *
 * **`XMLHttpRequest`, not `fetch`** - and that is not a legacy detail. `fetch` still cannot report
 * request *upload* progress in any browser, and a 200 MB transfer with no feedback is
 * indistinguishable from a hang. The same reason the remote console's clipboard panel uses XHR.
 *
 * Its own transport rather than `request`, so the token is attached here: `ensureFresh` runs first so
 * a long upload does not start on a token that is about to expire - the pre-emptive refresh cannot
 * help mid-transfer, and a 401 half way through 200 MB is not a retry anybody wants.
 */
export async function uploadFiles(
  path: string,
  files: readonly File[],
  onProgress?: (fraction: number) => void,
  fields: Readonly<Record<string, string>> = {}
): Promise<string> {
  await ensureFresh();

  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  for (const file of files) form.append('files', file, file.name);

  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${path}`);
    const token = bearer();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText);
        return;
      }
      // The server answers a refused transfer with its own sentence - "This transfer is larger than
      // the 200 MB limit" - and the status code alone would throw away the only useful part.
      reject(new ApiError(xhr.status, errorFrom(xhr.responseText) ?? `${xhr.status} ${xhr.statusText}`));
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'The upload could not reach the server.')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'The upload was cancelled.')));
    xhr.send(form);
  });
}
